import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SelectionContext } from '../context/SelectionContext';
import { useUser } from '../context/UserContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Numeros.css';

const Numeros = () => {
  const { choice } = useParams();
  const navigate = useNavigate();
  const { selections, updateSelections } = useContext(SelectionContext);
  const { userData, loading: userLoading } = useUser();
  const rota = window.location.pathname.split('/')[1];
  const [selectedNumbers, setSelectedNumbers] = useState(selections[rota]?.numbers || []);
  const [jsonData, setJsonData] = useState([]);
  const [totalParImpar, setTotalParImpar] = useState({ pares: 0, impares: 0 });
  const [buttonsDisabled, setButtonsDisabled] = useState(true);
  const [probabilidade, setProbabilidade] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dailyGenerations, setDailyGenerations] = useState(7);
  const [limitReached, setLimitReached] = useState(false);

  // Função para gerar dezenas - useCallback
  const gerarDezenas = useCallback((type) => {
    let numbers = [];
    if (type.toLowerCase() === 'par') {
      numbers = Array.from({ length: 12 }, (_, i) => 2 + i * 2);
    } else if (type.toLowerCase() === 'impar') {
      numbers = Array.from({ length: 13 }, (_, i) => 1 + i * 2).filter(num => num <= 25);
    }
    return numbers;
  }, []);

  // Função para calcular probabilidade - useCallback para evitar recriações
  const calcularProbabilidade = useCallback((pares, impares) => {
    const totalPares = 12;
    const totalImpares = 13;
    const combinacao = (n, k) => {
      if (k === 0 || k === n) return 1;
      let numerador = 1;
      for (let i = 0; i < k; i++) {
        numerador *= (n - i);
      }
      let denominador = 1;
      for (let i = 1; i <= k; i++) {
        denominador *= i;
      }
      return numerador / denominador;
    };
    const probabilidade = choice.toLowerCase() === 'par'
      ? combinacao(totalPares, pares)
      : combinacao(totalImpares, impares);
    setProbabilidade(probabilidade);
  }, [choice]);

  // Função para obter schema - useCallback
  const getSchema = useCallback((rota, type) => {
    const schemaMap = {
      '7-8': { par: 7, impar: 8 },
      '8-7': { par: 8, impar: 7 },
      '6-9': { par: 6, impar: 9 },
      '9-6': { par: 9, impar: 6 },
      '5-10': { par: 5, impar: 10 },
      '10-5': { par: 10, impar: 5 },
    };
    return schemaMap[rota]?.[type];
  }, []);

  const handleNumberSelect = (number) => {
    // Se acesso restrito, não permite interação
    if (error && error.includes('Acesso Restrito')) {
      return;
    }

    const maxPar = getSchema(rota, 'par');
    const maxImpar = getSchema(rota, 'impar');

    if (maxPar === undefined || maxImpar === undefined) {
      console.error('Invalid schema for rota:', rota);
      return;
    }

    let updatedNumbers;
    if (selectedNumbers.includes(number)) {
      updatedNumbers = selectedNumbers.filter(num => num !== number);
    } else {
      const currentPares = selectedNumbers.filter(num => num % 2 === 0).length;
      const currentImpares = selectedNumbers.filter(num => num % 2 !== 0).length;

      if (number % 2 === 0 && currentPares >= maxPar) return;
      if (number % 2 !== 0 && currentImpares >= maxImpar) return;

      updatedNumbers = [...selectedNumbers, number];
    }

    const totalPares = updatedNumbers.filter(num => num % 2 === 0).length;
    const totalImpares = updatedNumbers.filter(num => num % 2 !== 0).length;

    const isCorrectSelection = choice.toLowerCase() === 'par'
      ? totalPares === maxPar
      : totalImpares === maxImpar;
    setSelectedNumbers(updatedNumbers);
    setTotalParImpar({ pares: totalPares, impares: totalImpares });
    updateSelections(rota, choice, updatedNumbers);
    setButtonsDisabled(!isCorrectSelection);
    calcularProbabilidade(totalPares, totalImpares);
  };

  const handleClear = () => {
    // Se acesso restrito, não permite interação
    if (error && error.includes('Acesso Restrito')) {
      return;
    }

    setSelectedNumbers([]);
    setTotalParImpar({ pares: 0, impares: 0 });
    setProbabilidade(0);
    updateSelections(rota, choice, []);
    setButtonsDisabled(true);
  };

  // ✅ DEBUG DETALHADO - Verificar estado do usuário
  console.log('🔍 [DEBUG DETALHADO] Estado do usuário:', {
    userData,
    userDataTipo: typeof userData,
    userDataPlan: userData?.plan,
    userLoading,
    userDataEhNull: userData === null,
    userDataEhUndefined: userData === undefined,
    deveMostrarFree: (userData === null || userData?.plan === 'free'),
    deveMostrarPremium: userData?.plan === 'premium'
  });

  // ✅ DEBUG: Monitorar userData
  useEffect(() => {
    console.log('🔍 Numeros - userData atualizado:', {
      hasUserData: !!userData,
      plan: userData?.plan,
      email: userData?.email,
      userLoading
    });
  }, [userData, userLoading]);

  // ✅ INICIALIZAR CONTAGEM DIÁRIA - CORRIGIDO
  useEffect(() => {
    if (!userLoading && userData) {
      console.log('📅 Inicializando contador diário para:', userData.plan);

      if (userData.plan === 'free') {
        const today = new Date().toDateString();
        const storedData = localStorage.getItem('lotopim_daily_limit');

        if (storedData) {
          try {
            const { date, generations } = JSON.parse(storedData);
            if (date === today) {
              setDailyGenerations(parseInt(generations) || 7);
              setLimitReached(parseInt(generations) <= 0);
              console.log(`📊 Contador recuperado: ${generations} gerações restantes`);
            } else {
              // Novo dia, resetar contador
              setDailyGenerations(7);
              setLimitReached(false);
              localStorage.setItem('lotopim_daily_limit', JSON.stringify({
                date: today,
                generations: 7
              }));
              console.log('🔄 Novo dia - contador resetado para 7');
            }
          } catch (error) {
            console.error('❌ Erro ao recuperar contador:', error);
            setDailyGenerations(7);
            localStorage.setItem('lotopim_daily_limit', JSON.stringify({
              date: today,
              generations: 7
            }));
          }
        } else {
          // Primeiro acesso
          setDailyGenerations(7);
          localStorage.setItem('lotopim_daily_limit', JSON.stringify({
            date: today,
            generations: 7
          }));
          console.log('🎯 Primeiro acesso - contador definido como 7');
        }
      }
    }
  }, [userData, userLoading]);

  // ✅ VERIFICAÇÃO DE ACESSO - ROTAS PROTEGIDAS (CORRIGIDO)
  useEffect(() => {
    if (userLoading) {
      console.log('⏳ Aguardando carregamento do userData...');
      return;
    }

    if (!userData) {
      console.log('⚠️ userData não disponível ainda');
      return;
    }

    console.log('🔒 Verificando acesso para:', userData.plan);

    // CORREÇÃO: Removida a variável não utilizada 'freeAllowedRoutes'
    // Rotas exclusivas para Premium
    const premiumRoutes = ['8-7', '6-9', '9-6', '5-10', '10-5'];

    // Se o usuário é Free e tentou acessar uma rota premium via URL
    if (userData.plan === 'free' && premiumRoutes.includes(rota)) {
      console.log('🚫 Acesso negado - Rota premium para usuário free');
      setError('🔒 Acesso Restrito - Esta funcionalidade é exclusiva para assinantes Premium');
      setLoading(false);

      const timer = setTimeout(() => {
        navigate('/home');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [rota, userData, userLoading, navigate]);

  // Effect principal para carregar JSON
  useEffect(() => {
    if (error && error.includes('Acesso Restrito')) {
      return;
    }

    const validRotas = ['7-8', '8-7', '6-9', '9-6', '5-10', '10-5'];

    if (!validRotas.includes(rota)) {
      setError('Rota inválida');
      setLoading(false);
      return;
    }

    const getJsonFile = (rota, choice) => {
      const fileMap = {
        '7-8': { par: '/7_8_Impares.json', impar: '/7_8_Pares.json' },
        '8-7': { par: '/8_7_Impares.json', impar: '/8_7_Pares.json' },
        '6-9': { par: '/6_9_Impares.json', impar: '/6_9_Pares.json' },
        '9-6': { par: '/9_6_Impares.json', impar: '/9_6_Pares.json' },
        '5-10': { par: '/5_10_Impares.json', impar: '/5_10_Pares.json' },
        '10-5': { par: '/10_5_Impares.json', impar: '/10_5_Pares.json' },
      };
      return fileMap[rota]?.[choice.toLowerCase()];
    };

    const jsonFile = getJsonFile(rota, choice);

    if (!jsonFile) {
      console.error('JSON file not found for the given rota and choice:', rota, choice);
      setError('Arquivo de dados não encontrado para esta configuração');
      setLoading(false);
      return;
    }

    fetch(jsonFile)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        let key = `${rota}_${choice.toLowerCase() === 'par' ? 'Impares' : 'Pares'}`;
        key = `Contar_Ocorrencias_${key.replace('-', '_')}`;

        if (data.dataroot && data.dataroot[key]) {
          const formattedData = data.dataroot[key].map(item =>
            Object.values(item).map(value => parseInt(value, 10))
          );
          setJsonData(formattedData);
          console.log(`📁 JSON carregado: ${formattedData.length} jogos disponíveis`);
        } else {
          console.error('Key not found in JSON data:', key);
          setError(`Estrutura de dados inválida para ${rota} ${choice}`);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching JSON data:', error);
        setError('Erro ao carregar dados. Verifique sua conexão.');
        setLoading(false);
      });
  }, [choice, rota, error]);

  // ✅ CORREÇÃO CRÍTICA: Função gerarJogos com verificação robusta
  const gerarJogos = () => {
    if (error && error.includes('Acesso Restrito')) {
      return [];
    }

    if (selectedNumbers.length === 0) return [];

    console.log(`📊 Geração de jogos: Plano ${userData?.plan}, JSON tem ${jsonData.length} jogos`);

    let jogosPermitidos;

    // ✅ CORREÇÃO: Considera userData null como Free
    if (!userData || userData.plan === 'free') {
      jogosPermitidos = Math.min(jsonData.length, 7);
      console.log(`🔒 Free: Limitando a ${jogosPermitidos} jogos`);
    } else {
      jogosPermitidos = jsonData.length;
      console.log(`💎 Premium: Gerando todos os ${jogosPermitidos} jogos`);
    }

    const jogosGerados = jsonData.slice(0, jogosPermitidos).map(line =>
      [...selectedNumbers, ...line].slice(0, 15).sort((a, b) => a - b)
    );

    console.log(`🎯 Total de jogos gerados: ${jogosGerados.length}`);
    return jogosGerados;
  };

  // ✅ FUNÇÃO PARA ATUALIZAR CONTADOR DIÁRIO - CORRIGIDA
  const updateDailyGenerations = () => {
    if (userData?.plan === 'free') {
      const newCount = dailyGenerations - 1;
      console.log(`📉 Atualizando contador diário: ${dailyGenerations} -> ${newCount}`);

      setDailyGenerations(newCount);
      setLimitReached(newCount <= 0);

      const today = new Date().toDateString();
      localStorage.setItem('lotopim_daily_limit', JSON.stringify({
        date: today,
        generations: newCount
      }));

      return newCount;
    }
    return dailyGenerations;
  };

  // ✅ Função para criar jogos - CORRIGIDA com verificações robustas
  const handleCreateJogos = () => {
    if (error && error.includes('Acesso Restrito')) {
      return;
    }

    console.log(`🔍 Verificando limite: Plano ${userData?.plan}, Gerações restantes: ${dailyGenerations}`);

    // ✅ VERIFICAÇÃO ROBUSTA do limite diário
    if (userData?.plan === 'free') {
      if (dailyGenerations <= 0) {
        console.log('🚫 Limite diário atingido!');
        setLimitReached(true);
        alert('Você atingiu o limite diário de geração de jogos!');
        return;
      }
      console.log(`✅ Free: ${dailyGenerations} gerações restantes - permitindo geração`);
    } else {
      console.log('✅ Premium: Geração ilimitada permitida');
    }

    const jogosGerados = gerarJogos();

    console.log(`✅ Jogos a serem gerados: ${jogosGerados.length}`);

    if (jogosGerados.length > 0) {
      // ✅ ATUALIZAR CONTADOR DIÁRIO APENAS PARA FREE
      if (userData?.plan === 'free') {
        updateDailyGenerations();
      }

      updateSelections(rota, choice, jogosGerados);
      navigate('/jogos-gerados', {
        state: {
          jogos: jogosGerados,
          choice,
          rota,
          isFreeUser: userData?.plan === 'free',
          remainingGenerations: userData?.plan === 'free' ? dailyGenerations - 1 : null
        }
      });
    } else {
      alert('Nenhum jogo foi gerado. Verifique suas seleções.');
    }
  };

  // ✅ Loading enquanto userData não está pronto
  if (userLoading) {
    return (
      <div className="container">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando dados do usuário...</span>
          </div>
          <p className="ms-2">Carregando dados do usuário...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-danger text-center" role="alert">
          <h4 className="alert-heading">🔒 Acesso Não Autorizado</h4>
          <p>{error}</p>
          {error.includes('Acesso Restrito') && (
            <>
              <p className="mb-3">
                <strong>Upgrade para Premium e desbloqueie:</strong>
              </p>
              <ul className="text-start">
                <li>Estratégias 8-7, 6-9, 9-6, 5-10, 10-5</li>
                <li>Geração ilimitada de jogos</li>
                <li>Ferramenta Surpresinha Premium</li>
              </ul>
              <button
                className="btn btn-warning mt-2"
                onClick={() => window.open('https://pag.ae/8173yd97G', '_blank')}
              >
                💎 Fazer Upgrade para Premium
              </button>
              <div className="mt-3">
                <small>Redirecionando para a página inicial em 3 segundos...</small>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ✅ CORREÇÃO: Use a função gerarDezenas que está definida
  const numerosFixados = gerarDezenas(choice);

  return (
    <div className="numeros-container">
      <div className="numeros-header">
        <h1>Dezenas Fixas: {choice.charAt(0).toUpperCase() + choice.slice(1)}</h1>
        <p className="text-muted">Estratégia {rota} - Par/Ímpar</p>
      </div>

      {/* Informação sobre o plano - CORREÇÃO DEFINITIVA */}
      {!userLoading && userData !== undefined && (
        <div className={`plan-info ${(userData === null || userData?.plan === 'free') ? 'plan-free' : 'plan-premium'}`}>
          {userData === null || userData?.plan === 'free'
            ? `📊 Plano Free: ${dailyGenerations} gerações restantes hoje`
            : '⭐ Plano Premium Ativo - Geração Ilimitada'
          }
        </div>
      )}

      {/* ✅ MENSAGEM DE LIMITE ATINGIDO */}
      {limitReached && (
        <div className="alert alert-warning text-center">
          <h5>🚫 Limite Diário Atingido!</h5>
          <p>Você atingiu o limite diário de geração de jogos.</p>
          <p>
            <strong>Volte amanhã ou faça upgrade para Premium!</strong>
          </p>
          <button
            className="btn btn-warning mt-2"
            onClick={() => window.open('https://pag.ae/8173yd97G', '_blank')}
          >
            💎 Fazer Upgrade para Premium
          </button>
        </div>
      )}

      <div className="numeros-grid">
        {numerosFixados.map((numero) => (
          <button
            key={numero}
            className={`btn btn-number ${selectedNumbers.includes(numero) ? 'selected' : ''}`}
            onClick={() => handleNumberSelect(numero)}
            disabled={limitReached}
          >
            {numero}
          </button>
        ))}
      </div>

      <div className="numeros-stats">
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-value">{totalParImpar.pares}</div>
            <div className="stat-label">Pares</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{totalParImpar.impares}</div>
            <div className="stat-label">Ímpares</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">1:{probabilidade}</div>
            <div className="stat-label">Probabilidade</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">
              {userData?.plan === 'free' ? '7' : jsonData.length}
            </div>
            <div className="stat-label">Jogos/Geração</div>
          </div>
        </div>
      </div>

      <div className="action-buttons">
        <button
          id="gerarJogos"
          type="button"
          className="btn btn-primary-custom"
          onClick={handleCreateJogos}
          disabled={buttonsDisabled || ((!userData || userData.plan === 'free') && limitReached)}
        >
          {!userData || userData?.plan === 'free'
            ? `🎯 Gerar Jogos (${dailyGenerations} restantes)`
            : '🎯 Gerar Jogos'
          }
        </button>
        <button
          id="limpar"
          type="button"
          className="btn btn-danger-custom"
          onClick={handleClear}
          disabled={limitReached}
        >
          🗑️ Limpar
        </button>
      </div>

      {/* Informação adicional sobre limites */}
      {userData?.plan === 'free' && !limitReached && (
        <div className="alert alert-info mt-3">
          <small>
            <strong>💡 Informação:</strong> No plano Free você pode gerar até 7 lotes de jogos por dia.
            Cada geração contém no máximo 7 jogos.
            {dailyGenerations > 0 && ` Você tem ${dailyGenerations} gerações restantes hoje.`}
          </small>
        </div>
      )}
    </div>
  );
};

export default Numeros;